import clsx from 'clsx';
import { CurrencyInput } from 'components/CurrencyInput';
import { Checkbox } from 'components/EnumInput';
import { OptionalTextArea } from 'components/OptionalTextArea';
import { TextField } from 'components/TextField';
import React, { FC, useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@clover-util/use-debounce';

import { GIFT_AMOUNT_MAX, GIFT_AMOUNT_MIN } from 'routes/GiftCard/const';
import { validateEmail } from 'util/Regex';
import styles from './styles.module.scss';
import { GiftCardFormValues } from '../model';
import { MENU_ITEM_MODAL_TEST_ID } from 'routes/MenuItemModal/MenuItemModalTestIds';

type GiftCardInputFieldsProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'value'> & {
  value?: Partial<GiftCardFormValues>;
  onChange?: (values: GiftCardFormValues) => void;
};

export const GiftCardInputFields: FC<GiftCardInputFieldsProps> = memo(function GiftCardInputFields({ value, onChange, className, children, ...props }) {
  const { t } = useTranslation();

  const [amount, setAmount] = useState(value?.amount ?? GIFT_AMOUNT_MIN);
  const [note, setNote] = useState(value?.note ?? '');
  const [name, setName] = useState(value?.name ?? '');
  const [email, setEmail] = useState(value?.email ?? '');
  const [isCustomer, setIsCustomer] = useState(value?.isCustomer ?? false);

  const triggerOnChange = useDebounce((values: GiftCardFormValues) => { onChange?.(values); }, 240, [onChange]);

  useEffect(() => {
    triggerOnChange({ amount, note, name, email, isCustomer });
  }, [amount, email, isCustomer, name, note, triggerOnChange]);

  return (
    <div className={clsx(styles.formBody, className)} {...props}>
      <CurrencyInput
        className={clsx(styles.inputField)}
        max={GIFT_AMOUNT_MAX}
        min={GIFT_AMOUNT_MIN}
        onValueChange={setAmount}
        value={amount}
        noCents
      />
      <Checkbox
        className={styles.inputField}
        label={t('giftCards.buyForYourself')}
        value={t('giftCards.buyForYourself')}
        name="buyForYourself"
        data-testid={MENU_ITEM_MODAL_TEST_ID.checkbox}
        checked={isCustomer}
        onChange={e => setIsCustomer(e.target.checked)}
      />
      {!isCustomer && (
        <>
          <TextField
            className={styles.inputField}
            label={t('recipient.fullName')}
            onChange={e => setName(e.target.value)}
            required
            name="fullName"
            data-testid={MENU_ITEM_MODAL_TEST_ID.recipientFullname}
            value={name}
          />
          <TextField
            className={styles.inputField}
            label={t('recipient.email')}
            onChange={e => setEmail(e.target.value)}
            required
            name="email"
            data-testid={MENU_ITEM_MODAL_TEST_ID.recipientEmail}
            validateField={emailAddress => validateEmail(emailAddress) || t<string>('contactForm.invalidEmail')}
            value={email}
          />
          <OptionalTextArea
            className={styles.inputField}
            onChange={e => setNote(e.target.value)}
            name="note"
            data-testid={MENU_ITEM_MODAL_TEST_ID.note}
            value={note}
          >
            {t('recipient.message')}
          </OptionalTextArea>
        </>
      )}
    </div>
  );
});
